import {
    CompanyRole,
    ReportRole,
    ReportVersionStatus,
    ReportVersionTransition,
    ReportVulnerabilityStatus,
    TestType,
    VulnerabilitySeverity,
} from "app/api/graph/types";

export const companyRoleLabel = (value: CompanyRole): string => {
    switch (value) {
        case CompanyRole.Owner:
            return "Owner";
        case CompanyRole.Standard:
            return "Standard";
    }
};

export const reportRoleLabel = (value: ReportRole): string => {
    switch (value) {
        case ReportRole.Standard:
            return "Standard";
        case ReportRole.Limited:
            return "Limited";
    }
};

export const reportVersionStatusLabel = (value: ReportVersionStatus): string => {
    switch (value) {
        case ReportVersionStatus.Scoping:
            return "Scoping";
        case ReportVersionStatus.Prerequisites:
            return "Prerequisites";
        case ReportVersionStatus.Booked:
            return "Booked";
        case ReportVersionStatus.Testing:
            return "Testing";
        case ReportVersionStatus.Reporting:
            return "Reporting";
        case ReportVersionStatus.QualityAssurance:
            return "Quality assurance";
        case ReportVersionStatus.Published:
            return "Published";
    }
};

export const vulnerabilitySeverityLabel = (value: VulnerabilitySeverity): string => {
    switch (value) {
        case VulnerabilitySeverity.Critical:
            return "Critical";
        case VulnerabilitySeverity.High:
            return "High";
        case VulnerabilitySeverity.Medium:
            return "Medium";
        case VulnerabilitySeverity.Low:
            return "Low";
        case VulnerabilitySeverity.Informational:
            return "Informational";
    }
};

export const vulnerabilitySeverityColor = (value: VulnerabilitySeverity): string => {
    switch (value) {
        case VulnerabilitySeverity.Critical:
            return "#702fa0";
        case VulnerabilitySeverity.High:
            return "#c00000";
        case VulnerabilitySeverity.Medium:
            return "#ffc003";
        case VulnerabilitySeverity.Low:
            return "#00b050";
        case VulnerabilitySeverity.Informational:
            return "#1389ef";
    }
};

export const reportVulnerabilityStatusLabel = (value: ReportVulnerabilityStatus): string => {
    switch (value) {
        case ReportVulnerabilityStatus.Open:
            return "Open";
        case ReportVulnerabilityStatus.Resolved:
            return "Resolved (unverified)";
        case ReportVulnerabilityStatus.RiskAccepted:
            return "Risk accepted";
        case ReportVulnerabilityStatus.VerifiedResolved:
            return "Resolved (verified)";
        case ReportVulnerabilityStatus.Informational:
            return "Informational";
    }
};

export const reportVulnerabilityStatusColor = (value: ReportVulnerabilityStatus): string => {
    switch (value) {
        case ReportVulnerabilityStatus.Open:
            return "#c00000";
        case ReportVulnerabilityStatus.Resolved:
        case ReportVulnerabilityStatus.VerifiedResolved:
            return "#00b050";
        case ReportVulnerabilityStatus.RiskAccepted:
            return "#ffc003";
        case ReportVulnerabilityStatus.Informational:
            return "#1389ef";
    }
};

export const testTypeLabel = (value: TestType): string => {
    switch (value) {
        case TestType.WebApp:
            return "Web app";
        case TestType.InternalInfrastructure:
            return "Internal infrastructure";
        case TestType.ExternalInfrastructure:
            return "External infrastructure";
        case TestType.Wifi:
            return "WiFi";
        case TestType.Phishing:
            return "Phishing";
        case TestType.RedTeamAssessment:
            return "Red team assessment";
        case TestType.InternalExternalInfra:
            return "Internal & external infra";
        case TestType.AppInfraCloud:
            return "App & infra & cloud";
        case TestType.CloudAssessment:
            return "Cloud assessment";
        case TestType.PenetrationTest:
            return "Penetration test";
        case TestType.Llm:
            return "LLM";
        case TestType.DesktopApp:
            return "Desktop app";
        case TestType.MobileApp:
            return "Mobile app";
        case TestType.Api:
            return "API";
        case TestType.PurpleTeamAssessment:
            return "Purple team assessment";
        case TestType.Kubernetes:
            return "Kubernetes";
        case TestType.SapTesting:
            return "SAP testing";
        case TestType.PhysicalPenetrationTesting:
            return "Physical penetration testing";
        case TestType.BlackBox:
            return "Black box";
        case TestType.GreyBox:
            return "Grey box";
        case TestType.WhiteBox:
            return "White box";
        case TestType.SocialEngineering:
            return "Social engineering";
        case TestType.BlindTesting:
            return "Blind testing";
        case TestType.DoubleBlindTesting:
            return "Double-blind testing";
        case TestType.IotAndEmbedded:
            return "IoT & embedded";
        case TestType.NotSpecified:
            return "Not specified";
    }
};

export const reportVersionStatusTransitionLabel = (value: ReportVersionTransition): string => {
    switch (value) {
        case ReportVersionTransition.CompleteScoping:
            return "Complete scoping";
        case ReportVersionTransition.Book:
            return "Book";
        case ReportVersionTransition.StartTest:
            return "Start test";
        case ReportVersionTransition.CompleteTest:
            return "Complete test";
        case ReportVersionTransition.CompleteReport:
            return "Complete report";
        case ReportVersionTransition.Publish:
            return "Publish";
        case ReportVersionTransition.RequestScoping:
            return "Request scoping";
        case ReportVersionTransition.RequestPrerequisites:
            return "Request prerequisites";
    }
};

export const reportVersionStatusTransitionWarning = (value: ReportVersionTransition): string => {
    switch (value) {
        case ReportVersionTransition.CompleteScoping:
            return "Do you really want to complete scoping? The scoping sections will no longer be editable.";
        case ReportVersionTransition.Book:
            return "Do you really want to mark the test as booked?";
        case ReportVersionTransition.StartTest:
            return "Do you really want to mark the test as started?";
        case ReportVersionTransition.CompleteTest:
            return "Do you really want to mark the test as complete? It will no longer be possible to go back to previous statuses.";
        case ReportVersionTransition.CompleteReport:
            return "Do you really want to mark the report as complete? It will no longer be possible to go back to previous statuses.";
        case ReportVersionTransition.Publish:
            return "Do you really want to publish this report version? The report will become available to all company and invited viewers, who will be notified by email. It will no longer be possible to go back to previous statuses.";
        case ReportVersionTransition.RequestScoping:
            return "Do you really want to move back to scoping?";
        case ReportVersionTransition.RequestPrerequisites:
            return "Do you really want to move back to prerequisites?";
    }
};
